import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import { BookmarkIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getEnrollmentGuide } from '../../../api/enrollments/get-enrollment-guide'
import {
  ClientHeader,
  getGuideItems,
  GuideDisplayKeys,
  GuideItem
} from '../../../components/pages/language-test/client-space/shared-client-space-ui'
import useEnrollment from '../../../lib/hooks/use-enrollment'
import { accomodationEnumMapping } from '../../../lib/types/enums/accomodation-type/accomodation-type'
import { ApplicationStatusEnum } from '../../../lib/types/enums/enrollment/application-status.enum'
import { EnrollmentPropositionTypeEnum } from '../../../lib/types/enums/enrollment/enrollment-proposition-type.enum'
import { enrollmentGuideFields } from '../../../lib/types/fields/guides/enrollment-guide'
import { EnrollmentProperties } from '../../../lib/types/models/enrollment/enrollment.properties'

const HOST_FAMILY_ITEMS: GuideDisplayKeys[] = [
  'formalities',
  'preparation',
  'family-hostess',
  'trip',
  'courses-activities',
  'security-support'
]

const COLLEGE_ITEMS: GuideDisplayKeys[] = [
  'formalities',
  'preparation',
  'daily-life',
  'trip',
  'courses-activities',
  'security-support'
]

export default function ClientLecGuidePage() {
  const { enrollment } = useEnrollment()

  const isHostFamily = EnrollmentProperties.isHostFamily(enrollment)
  const accomodationCategory = EnrollmentProperties.getEnrollmentAccomodation(enrollment)
  const selectedGuideItems = isHostFamily ? HOST_FAMILY_ITEMS : COLLEGE_ITEMS
  const isAffected =
    enrollment.propositionType === EnrollmentPropositionTypeEnum.NORMAL &&
    enrollment.status === ApplicationStatusEnum.AFFECTED
  const canViewGuide =
    isAffected ||
    (enrollment.propositionType === EnrollmentPropositionTypeEnum.OTO_INDIVIDUAL &&
      enrollment.status === ApplicationStatusEnum.CONFIRMED)

  const {
    data: guide,
    isSuccess,
    isPending
  } = useQuery({
    queryKey: ['guide', enrollment?.id, enrollmentGuideFields],
    queryFn: () => getEnrollmentGuide(enrollment.id, enrollmentGuideFields),
    enabled: !!enrollment?.id && canViewGuide
  })

  // no guide is published for this stay yet: wait for the answer so the full layout never flashes
  if (!canViewGuide || isPending || (isSuccess && !guide))
    return (
      <div className={'flex flex-col gap-4'}>
        <ClientHeader title={'Guide LEC'} icon={BookmarkIcon} />
        <Box className={'border border-grey-light rounded-lg p-4'}>
          <Typography variant={'secondary-body'}>
            Le guide LEC sera disponible prochainement
          </Typography>
        </Box>
      </div>
    )

  return (
    <div className={'flex flex-col gap-4'}>
      <ClientHeader title={'Guide LEC'} icon={BookmarkIcon} />

      <Box
        className={'border border-grey-light flex-row gap-4 flex justify-between rounded-lg p-0'}
      >
        <div className={'flex flex-col gap-8 p-4 h-auto'}>
          {accomodationCategory && (
            <Typography variant={'h1'}>
              Séjours en{' '}
              <Typography
                variant={'h1'}
                color={tailwindColors.primary.DEFAULT}
                className={'inline-block'}
              >
                {accomodationEnumMapping[accomodationCategory]}
              </Typography>{' '}
              en Europe
            </Typography>
          )}

          <div className={'flex flex-col gap-4'}>
            <Typography variant={'sub-heading'} color={tailwindColors.primary.DEFAULT}>
              Le guide à lire avant le départ !
            </Typography>
            <Typography variant={'tag'}>
              Tout ce qu'il faut savoir pour un séjour linguistique LEC réussi :
            </Typography>

            <div className={'flex flex-col gap-4'}>
              {getGuideItems(selectedGuideItems).map((item) => (
                <GuideItem {...item} key={item.key} />
              ))}
            </div>
          </div>

          {guide && (
            <Button
              className={'mobile:w-full w-fit bg-red text-white'}
              variant={'destructive'}
              label={'Découvrir le guide complet'}
              onClick={() => window.open(guide.pdf.path, '_blank')}
            />
          )}
        </div>

        <img
          src={'/lec-guide.png'}
          alt={'Guide LEC'}
          className={'rounded-r-lg h-full mobile:hidden tab:hidden smallLaptop:hidden'}
        />
      </Box>
    </div>
  )
}
